import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { X, Plus, ArrowRight, Globe2 } from "lucide-react";

/**
 * RoutingInput
 * Multi-leg routing: paste "KOPF-TNCM-KOPF-KTEB-EGLL-LFMN-KOPF" (dash/space/comma-separated)
 * or type one ICAO at a time. Each stop is resolved to city/country.
 * Reports back { routing: string[], isInternational: bool } via onChange.
 */
export default function RoutingInput({ value = [], onChange, testId }) {
  const [text, setText] = useState("");
  const [meta, setMeta] = useState({});   // icao → {city, country}
  const [intl, setIntl] = useState(false);

  useEffect(() => {
    if (!value?.length) { setIntl(false); return; }
    let cancelled = false;
    (async () => {
      const seen = { ...meta };
      const results = await Promise.all(
        value.map(async (icao) => {
          if (seen[icao]) return seen[icao];
          try { const r = await api.get(`/airports/${encodeURIComponent(icao)}`); return r.data; }
          catch { return { icao, city: icao, country: "?" }; }
        })
      );
      if (cancelled) return;
      const next = {};
      value.forEach((icao, i) => { next[icao] = results[i]; });
      setMeta(next);
      setIntl(results.some((a) => a && a.country && a.country !== "US"));
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (raw) => {
    if (!raw) return;
    const parts = raw
      .split(/[\s,\-→>]+/)
      .map((s) => s.trim().toUpperCase())
      .filter((s) => s.length >= 3 && s.length <= 4);
    if (!parts.length) return;
    onChange([...(value || []), ...parts]);
    setText("");
  };

  const remove = (idx) => {
    const next = [...value];
    next.splice(idx, 1);
    onChange(next);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " " || e.key === "-") {
      e.preventDefault();
      commit(text);
    } else if (e.key === "Backspace" && !text && value.length) {
      remove(value.length - 1);
    }
  };

  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-cyan/25 bg-black/30 p-3 min-h-[70px]">
        {value.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2 items-center">
            {value.map((icao, i) => {
              const m = meta[icao];
              const isNonUS = m?.country && m.country !== "US";
              return (
                <React.Fragment key={`${icao}-${i}`}>
                  <span
                    data-testid={testId ? `${testId}-chip-${i}` : undefined}
                    className={`inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full text-[12px] mono font-semibold border ${isNonUS ? "border-orange-hud/60 text-orange-hud bg-orange-hud/[0.10]" : "border-cyan/40 text-cyan bg-cyan/[0.08]"}`}
                    title={m ? `${m.name} · ${m.city}, ${m.country}` : icao}
                  >
                    {icao}
                    <span className="mono text-[10px] opacity-70">· {m?.city || "…"}</span>
                    <button
                      type="button"
                      onClick={() => remove(i)}
                      data-testid={testId ? `${testId}-remove-${i}` : undefined}
                      className="ml-0.5 hover:text-white p-0.5 rounded-full opacity-70 hover:opacity-100"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                  {i < value.length - 1 && <ArrowRight className="h-3 w-3 text-white/30" />}
                </React.Fragment>
              );
            })}
          </div>
        )}
        <div className="flex gap-2">
          <input
            data-testid={testId}
            className="input flex-1 !py-2 mono"
            value={text}
            onKeyDown={handleKey}
            onChange={(e) => setText(e.target.value)}
            onPaste={(e) => {
              const paste = e.clipboardData.getData("text");
              if (/[\s,\-→>]/.test(paste)) {
                e.preventDefault();
                commit(paste);
              }
            }}
            placeholder="Paste `KOPF-TNCM-KOPF-KTEB-EGLL-LFMN-KOPF` or type ICAOs one by one"
          />
          <button
            type="button"
            data-testid={testId ? `${testId}-add` : undefined}
            onClick={() => commit(text)}
            className="btn"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
      </div>

      {intl && (
        <div className="rounded-lg border border-orange-hud/40 bg-orange-hud/[0.08] p-3 flex items-start gap-3" data-testid={testId ? `${testId}-intl-banner` : undefined}>
          <Globe2 className="h-5 w-5 text-orange-hud flex-shrink-0 mt-0.5" />
          <div>
            <div className="mono text-orange-hud text-sm font-bold tracking-wider">🛂 INTL · BRING PASSPORT</div>
            <div className="text-xs text-white/70 mt-1">Route includes airports outside the United States. This is an international multi-leg trip.</div>
          </div>
        </div>
      )}
    </div>
  );
}
